import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {ColorPalettesContainer} from '@Components/auto-branding/components/color-palettes-container';
import {
  getBrandKitColorPalettes,
  getColorPalettes,
  getColorPalettesByTagIds,
  getUserColorPalettes,
  isSameColorPalette,
  removeDuplicateColorPalettes,
  changeColorPaletteType,
} from '@Libraries/auto-branding-email-maker';
import type {ColorPaletteInfo, ColorPalettePill} from '@Components/auto-branding/auto-branding.types';
import {ColorPaletteType} from '@Components/auto-branding/auto-branding.types';
import {ColorPaletteSearchSector} from '@Components/auto-branding/components/color-palette-search-sector';
import {redirectUser} from '@Utils/browser.util';
import {Button, IconPlacement, Size, Type} from '@Components/button';
import {Icon} from '@Components/icon-v2';
import {Text, TextSize} from '@Components/text';
import {IconSize} from '@Components/icon-v2/icon.types';
import {SkeletonBrick} from '@Components/skeleton-brick';
import {getUniqueString} from '@Utils/string.util';
import Divider from '@Components/divider';
import styles from './color-palette-panel.module.scss';
import {canUserAccessBrandKits, openUpsellingDialogForBrands} from '@Libraries/brands-library';

export interface ColorPalettePanelProps {
  onColorPaletteSelect: (colorPalette: ColorPaletteInfo) => void;
  appliedColorPalette: ColorPaletteInfo | null;
  colorPaletteAppliedEvent: string;
}

const FETCH_LIMIT_USER_PALETTES = 10;
const MAXIMUM_VISIBLE_PALETTES_RECENT = 3;
const MAXIMUM_VISIBLE_PALETTES_BRANDKIT = 3;

const SKELETON_BRICK_MAX_WIDTH = 252;
const SKELETON_BRICK_WIDTH_PERCENTAGES = [100, 55, 80, 100, 55];
const SKELETON_BRICK_HEIGHT = 24;
const SKELETON_BRICK_RADIUS = '4px';

export function ColorPalettePanel({onColorPaletteSelect, appliedColorPalette, colorPaletteAppliedEvent}: ColorPalettePanelProps): ReactElement {
  const [selectedColorPalette, setSelectedColorPalette] = useState<ColorPaletteInfo | null>(appliedColorPalette);
  const [recentColorPalettes, setRecentColorPalettes] = useState<ColorPaletteInfo[]>([]);
  const [recentColorPalettesLoaded, setRecentColorPalettesLoaded] = useState<boolean>(false);
  const [adminColorPalettes, setAdminColorPalettes] = useState<ColorPaletteInfo[]>([]);
  const [adminColorPalettesLoaded, setAdminColorPalettesLoaded] = useState<boolean>(false);
  const [brandKitPalettes, setBrandKitPalettes] = useState<ColorPaletteInfo[]>([]);
  const [brandKitPalettesLoaded, setBrandKitPalettesLoaded] = useState<boolean>(!canUserAccessBrandKits());
  const [showSearchResults, setShowSearchResults] = useState<boolean>(false);
  const [colorPalettesToDisplay, setColorPalettesToDisplay] = useState<ColorPaletteInfo[]>([]);
  const [showBackToTop, setShowBackToTop] = useState<boolean>(false);
  const [isColorPaletteBeingApplied, setIsColorPaletteBeingApplied] = useState<boolean>(false);
  const parentComponentRef = useRef<HTMLDivElement>(null);
  const backToTopComponentRef = useRef<HTMLDivElement>(null);

  const handlePaletteSelect = (newColorPalette: ColorPaletteInfo): void => {
    if (isColorPaletteBeingApplied) {
      return;
    }

    setIsColorPaletteBeingApplied(true);
    setSelectedColorPalette(newColorPalette);
    addColorPaletteToRecents(newColorPalette);
    onColorPaletteSelect(newColorPalette);
  };

  const getPillTagIds = (pills: ColorPalettePill[]): number[] => {
    return Array.from(
      new Set(
        pills.reduce((previousValue: number[], currentValue) => {
          return [
            ...previousValue,
            ...currentValue.tags.map((tag) => {
              return tag.id;
            }),
          ];
        }, [])
      )
    );
  };

  const addColorPaletteToRecents = (colorPalette: ColorPaletteInfo): void => {
    if (colorPalette.type === ColorPaletteType.User) {
      return;
    }

    const existsInBrandKit = brandKitPalettes.some((palette) => {
      return isSameColorPalette(palette, colorPalette);
    });

    if (existsInBrandKit) {
      return;
    }

    const existingIndex = recentColorPalettes.findIndex((palette) => {
      return isSameColorPalette(palette, colorPalette);
    });

    if (existingIndex !== -1) {
      const orderedColorPalettes = moveSelectedPaletteToFirstIndex(recentColorPalettes, colorPalette);
      setRecentColorPalettes(orderedColorPalettes);
      return;
    }

    if (recentColorPalettes.length > FETCH_LIMIT_USER_PALETTES) {
      recentColorPalettes.pop();
    }

    const newColorPalette = changeColorPaletteType(colorPalette, ColorPaletteType.User);
    setRecentColorPalettes([newColorPalette, ...recentColorPalettes]);
  };

  const getRecentsColorPaletteComponent = (): ReactElement | null => {
    if (showSearchResults) {
      return null;
    }

    if (recentColorPalettes.length === 0) {
      return null;
    }

    return (
      <ColorPalettesContainer
        heading={window.i18next.t('pmwjs_recents')}
        colorPalettes={recentColorPalettes}
        selectedPalette={selectedColorPalette}
        onColorPaletteSelected={handlePaletteSelect}
        maximumVisiblePalettes={MAXIMUM_VISIBLE_PALETTES_RECENT}
        colorPaletteBeingApplied={isColorPaletteBeingApplied}
        showPaletteName={false}
        showPaletteNameOnHover
      />
    );
  };

  const brandKitHandler = (): void => {
    if (!canUserAccessBrandKits()) {
      openUpsellingDialogForBrands();
      return;
    }

    redirectUser(window.PMW.util.site_url('posters/mine#/brands'), false, false);
  };

  const getAddBrandKitButton = (): ReactElement => {
    return (
      <div>
        <div className={styles.brandKitHeading}>
          <div className="flex-row-align-center">
            <Icon icon="icon-brands" className="icon" size={IconSize.SIZE_ICON_20} />
            <Text val={window.i18next.t('pmwjs_brand_kits')} size={TextSize.XSMALL} bold className="spacing-m-l-2" />
          </div>
          {!canUserAccessBrandKits() ? <Icon className={`icon ${styles.premiumIcon}`} icon="icon-crown" size={IconSize.SIZE_ICON_16} /> : ''}
        </div>
        <Divider additionalClasses="spacing-m-t-2" />
        <Button size={Size.SMALL} type={Type.DASHED} onClick={brandKitHandler} customClasses="spacing-m-t-4 _full-width">
          <Icon icon="icon-plus" className={styles.brandKitAddIcon} />
          <Text size={TextSize.XSMALL} val={window.i18next.t('pmwjs_add_a_brand')} className="_text-transform-none" />
        </Button>
      </div>
    );
  };

  const getBrandKitPaletteComponent = (): ReactElement | null => {
    if (showSearchResults) {
      return null;
    }

    if (brandKitPalettes.length === 0 || !canUserAccessBrandKits()) {
      return getAddBrandKitButton();
    }

    return (
      <ColorPalettesContainer
        heading={window.i18next.t('pmwjs_brand_kits')}
        colorPalettes={brandKitPalettes}
        maximumVisiblePalettes={MAXIMUM_VISIBLE_PALETTES_BRANDKIT}
        showPaletteName
        showPaletteNameOnHover={false}
        selectedPalette={selectedColorPalette}
        colorPaletteBeingApplied={isColorPaletteBeingApplied}
        onColorPaletteSelected={handlePaletteSelect}
      />
    );
  };

  const getNoSearchResultsComponent = (): ReactElement => {
    return (
      <div className={styles.noResultsContainer}>
        <Icon icon="icon-search" size={IconSize.SIZE_ICON_32} className={`${styles.iconContainer} spacing-p-4 spacing-m-t-5`} />
        <Text val={window.i18next.t('pmwjs_sorry_no_results')} size={TextSize.XSMALL} className="spacing-m-4 -text-align-center " />
        <Button
          size={Size.SMALL}
          type={Type.SECONDARY}
          text={window.i18next.t('pmwjs_back_to_styles')}
          textClasses="_text-transform-none"
          onClick={() => {
            setShowSearchResults(false);
          }}
        />
      </div>
    );
  };

  const getLoadingSkeleton = (): ReactElement => {
    return (
      <div className={styles.loadingSkeleton}>
        {SKELETON_BRICK_WIDTH_PERCENTAGES.map((widthPercentage) => {
          return <SkeletonBrick key={getUniqueString()} width={SKELETON_BRICK_MAX_WIDTH * (widthPercentage / 100)} height={SKELETON_BRICK_HEIGHT} radius={SKELETON_BRICK_RADIUS} />;
        })}
      </div>
    );
  };

  const getAsyncLoadedComponents = (): ReactElement => {
    if (!recentColorPalettesLoaded || !brandKitPalettesLoaded) {
      return getLoadingSkeleton();
    }

    return (
      <>
        {getRecentsColorPaletteComponent()}
        {getBrandKitPaletteComponent()}
      </>
    );
  };

  const getAdminColorPaletteComponent = (): ReactElement | null => {
    if (showSearchResults || !adminColorPalettesLoaded) {
      return null;
    }

    return (
      <ColorPalettesContainer
        heading={window.i18next.t('pmwjs_all_styles')}
        colorPalettes={adminColorPalettes}
        selectedPalette={selectedColorPalette}
        colorPaletteBeingApplied={isColorPaletteBeingApplied}
        onColorPaletteSelected={handlePaletteSelect}
        showPaletteNameOnHover
      />
    );
  };

  const getSearchColorPaletteComponent = (): ReactElement | null => {
    if (!showSearchResults) {
      return null;
    }

    if (colorPalettesToDisplay.length === 0) {
      return getNoSearchResultsComponent();
    }

    return (
      <ColorPalettesContainer
        heading={null}
        colorPalettes={colorPalettesToDisplay}
        selectedPalette={selectedColorPalette}
        colorPaletteBeingApplied={isColorPaletteBeingApplied}
        onColorPaletteSelected={handlePaletteSelect}
        showPaletteNameOnHover={false}
        showPaletteName
      />
    );
  };

  const handleSelectedPillsChange = (pills: ColorPalettePill[]): void => {
    setShowSearchResults(pills.length > 0);

    const tags = getPillTagIds(pills);
    if (tags.length === 0) {
      setColorPalettesToDisplay([]);
      return;
    }

    scrollPanelToTop();
    setColorPalettesToDisplay(getColorPalettesByTagIds(tags));
  };

  const getBackToTopButton = (): ReactElement => {
    return (
      <Button
        customClasses={styles.backToTopButton}
        size={Size.XSMALL}
        icon="icon-arrow-top"
        text={window.i18next.t('pmwjs_back_to_top')}
        iconPlacement={IconPlacement.LEFT}
        onClick={() => {
          scrollPanelToTop(true);
        }}
      />
    );
  };

  const moveSelectedPaletteToFirstIndex = (palettes: ColorPaletteInfo[], selectedPalette: ColorPaletteInfo | null): ColorPaletteInfo[] => {
    const selectedBrandKitIndex = palettes.findIndex((palette) => {
      return isSameColorPalette(palette, selectedPalette);
    });

    if (selectedBrandKitIndex === -1) {
      return palettes;
    }

    const selectedBrandKit = palettes[selectedBrandKitIndex];
    palettes.splice(selectedBrandKitIndex, 1);
    palettes.unshift(selectedBrandKit);

    return palettes;
  };

  const getIntersectionObserverForScroll = (): IntersectionObserver => {
    return new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setShowBackToTop(!entry.isIntersecting);
        });
      },
      {threshold: 1}
    );
  };

  const scrollPanelToTop = (smooth = false): void => {
    parentComponentRef.current?.scrollIntoView({block: 'start', behavior: smooth ? 'smooth' : 'instant'});
  };

  const colorPaletteAppliedEventHandler = (): void => {
    setIsColorPaletteBeingApplied(false);
  };

  useEffect(() => {
    void getColorPalettes().then((adminColorPalettesToShow) => {
      setAdminColorPalettes(adminColorPalettesToShow);
      setAdminColorPalettesLoaded(true);
    });

    void getBrandKitColorPalettes().then((palettes) => {
      setBrandKitPalettes(palettes);
      setBrandKitPalettesLoaded(true);
    });

    void getUserColorPalettes(FETCH_LIMIT_USER_PALETTES).then((palettes) => {
      const selectedPaletteToAdd: ColorPaletteInfo[] = selectedColorPalette ? [changeColorPaletteType(selectedColorPalette, ColorPaletteType.User)] : [];
      const recentPalettes = [...selectedPaletteToAdd, ...palettes];
      const uniquePalettes = removeDuplicateColorPalettes(recentPalettes);

      setRecentColorPalettes(uniquePalettes);
      setRecentColorPalettesLoaded(true);
    });

    const observer = getIntersectionObserverForScroll();
    observer.observe(backToTopComponentRef.current as Element);

    window.addEventListener(colorPaletteAppliedEvent, colorPaletteAppliedEventHandler);

    return (): void => {
      observer.disconnect();
      window.removeEventListener(colorPaletteAppliedEvent, colorPaletteAppliedEventHandler);
    };
  }, []);

  return (
    <div ref={parentComponentRef}>
      <div ref={backToTopComponentRef} />
      <ColorPaletteSearchSector
        onSearchSubmit={(searchTerm: string, colorPalettes: ColorPaletteInfo[]) => {
          setShowSearchResults(searchTerm !== '');
          setColorPalettesToDisplay(colorPalettes);
          if (searchTerm !== '') {
            scrollPanelToTop();
          }
        }}
        onSearchInput={(searchTerm: string) => {
          if (searchTerm === '') {
            setShowSearchResults(false);
          }
        }}
        onSelectedPillsChange={handleSelectedPillsChange}
        showSearchResults={showSearchResults}
        showShadow={showBackToTop}
        brandKitPalettes={brandKitPalettes}
      />
      <div className={styles.panelComponents}>
        {getAsyncLoadedComponents()}
        {getAdminColorPaletteComponent()}
        {getSearchColorPaletteComponent()}
      </div>
      <div className={`${styles.backToTopButtonContainer} ${!showBackToTop ? '_hidden' : ''}`}>{getBackToTopButton()}</div>
    </div>
  );
}
